import ExemptVrnsIcon from '@assets/drawer-icon/ExemptVrnsIcon';
import IOSSwitch from '@components/IOSSwitch';
import { VehicleExemptTag } from '@EcamModel/model/VehicleExempt';
import { Box, FormControlLabel, Link, Radio, RadioGroup, Stack, Tooltip, Typography } from '@mui/material';
import InformationIcon from '@pages/cameras/details/components/icon/InformationIcon';
import color from '@theme/Colors';
import _ from 'lodash';
import { OrangeStack } from './PCDetail';
import { GeoZones } from '@EcamModel/model';

export interface ExemptFutureEnforcementValue {
    IsEnabled: boolean;
    VehicleExemptTag: VehicleExemptTag | null;
}

interface Props {
    locationName: string;
    zoneNames: string[] | string;
    cameraName: string;
    cameraId: number;
    geoZone: GeoZones;
    value: ExemptFutureEnforcementValue;
    onChange: (nextValue: ExemptFutureEnforcementValue) => void;
}

export const EXEMPT_REASON_OPTIONS: VehicleExemptTag[] = [
    VehicleExemptTag.EmergencyVehicle,
    VehicleExemptTag.Bus,
    VehicleExemptTag.Taxi,
    VehicleExemptTag.BinLorriesRefuse,
    VehicleExemptTag.HGVS,
    VehicleExemptTag.BreakdownVehicle,
    VehicleExemptTag.ClientRequest,
];

export default function ExemptFutureEnforcementNotice({ locationName, zoneNames, cameraName, cameraId, geoZone, value, onChange }: Props) {
    const { IsEnabled: enabled, VehicleExemptTag: reason } = value;

    const cameraDetailsUrl = `/cameras/${cameraId}`;
    
    const geoZoneUrl = geoZone.CameraViewDetail?.Id 
        ? `/cameras/${cameraId}?tab=geozone&cameraViewId=${geoZone.CameraViewDetail.Id}`
        : `/cameras/${cameraId}?tab=geozone`;

    const handleToggle = (checked: boolean) => {
        onChange({
            IsEnabled: checked,
            VehicleExemptTag: checked ? reason || VehicleExemptTag.Bus : null,
        });
    };

    const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...value,
            VehicleExemptTag: !_.isNil(event.target.value) ? (event.target.value as VehicleExemptTag) : null,
        });
    };

    return (
        <OrangeStack spacing={2}>
            {/* Top row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                {/* Left side with icon and title */}
                <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
                    {/* Shield/Notification icon */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            backgroundColor: color.gold300,
                            color: color.gold600,
                        }}
                    >
                        <ExemptVrnsIcon />
                    </Box>

                    {/* Title and helper text */}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography color={color.gold800}>
                            Exempt vehicle from receiving a PCN for this specific Geo-Zone
                        </Typography>
                        <Tooltip
                            placement="top"
                            title={
                                <Box>
                                    {locationName && (
                                        <Typography color={color.white} fontSize={12}>
                                            Location: {locationName}
                                        </Typography>
                                    )}
                                    <Typography color={color.white} fontSize={12}>
                                        Zone(s): {zoneNames}
                                    </Typography>
                                    <Typography color={color.white} fontSize={12}>
                                        Camera:{' '}
                                        <Link
                                            href={cameraDetailsUrl}
                                            target="_blank"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                window.open(cameraDetailsUrl, '_blank');
                                            }}
                                            sx={{
                                                color: color.white,
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    color: color.grey200,
                                                },
                                            }}
                                        >
                                            {cameraName}
                                        </Link>
                                    </Typography>
                                    <Typography color={color.white} fontSize={12}>
                                        Geo-zone:{' '}
                                        <Link
                                            href={geoZoneUrl}
                                            target="_blank"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                window.open(geoZoneUrl, '_blank');
                                            }}
                                            sx={{
                                                color: color.white,
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    color: color.grey200,
                                                },
                                            }}
                                        >
                                            {geoZone.GeoZoneName || geoZone.Id}
                                        </Link>
                                    </Typography>
                                </Box>
                            }
                        >
                            <InformationIcon fontSize={16} color={color.gold800} />
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Right side with location info and switch */}
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IOSSwitch
                        label={
                            <Typography sx={{ minWidth: '55px' }}>
                                {!!enabled ? 'Enable' : 'Disable'}{' '}
                                <Typography component="span" sx={{ color: color.grey600, fontSize: 12, ml: 0.5 }}>
                                    (Alt+E toggle, Alt+1–7 select)
                                </Typography>
                            </Typography>
                        }
                        iosSwitchProps={{
                            checked: Boolean(enabled),
                            onChange(_, checked) {
                                handleToggle(checked);
                            },
                        }}
                    />
                </Stack>
            </Stack>

            {/* RadioGroup for exemption reasons - only visible when enabled */}
            {enabled && (
                <Box
                    sx={{
                        backgroundColor: color.white,
                        p: 2,
                        borderRadius: 1,
                    }}
                >
                    <RadioGroup value={reason ?? ''} onChange={handleReasonChange} aria-label="Exemption reason">
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                            }}
                        >
                            {EXEMPT_REASON_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio size="small" />}
                                    label={<Typography sx={{ whiteSpace: 'nowrap' }}>{option}</Typography>}
                                    sx={{ marginRight: 0 }}
                                />
                            ))}
                        </Box>
                    </RadioGroup>
                </Box>
            )}
        </OrangeStack>
    );
}
